import { PostOfficeAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import {
	type Context,
	isPostOfficePlacementsGloballyDisabled,
	usePostOfficeContext,
	usePostOfficeRoute,
	useStableObject,
} from '@atlassian/post-office-context';
import { UFOv1 } from '@atlassian/post-office-frontend-performance-tracking';
import { usePlacementFetchingContext } from '@atlassian/post-office-placement-data';
import React, {
	type FunctionComponent,
	type ReactNode,
	useCallback,
	useMemo,
	useContext,
} from 'react';
import { addError, addErrorToAll } from '@atlaskit/react-ufo/interaction-metrics';

import { ANALYTICS_SOURCE } from './constants';
import { requestInitialData } from './initialDataClient';
import { useBuildBasePath } from './utils/buildBasePath';

import UFOInteractionIDContext from '@atlaskit/react-ufo/interaction-id-context';
import UFOInteractionContext, { type LabelStack } from '@atlaskit/react-ufo/interaction-context';
import { fg } from '@atlaskit/platform-feature-flags';

interface BaseProps {
	placementId: string;
	consumptionType: 'remote' | 'local';
	disableInitialData?: boolean;
	onEvent?: PlacementOnEvent;
}

const useInitialDataIfEnabled = (
	placementId: string,
	context: Context,
	baseUrl: string | undefined,
	path: string,
	disableInitialData: boolean,
) => {
	const fetcher = usePlacementFetchingContext();

	const arePlacementsGloballyDisabled = isPostOfficePlacementsGloballyDisabled(context);

	return useMemo(
		() =>
			arePlacementsGloballyDisabled || disableInitialData
				? undefined
				: requestInitialData(placementId, context, fetcher.fetch, {
						baseUrl,
						path,
					}),
		[
			arePlacementsGloballyDisabled,
			disableInitialData,
			baseUrl,
			path,
			placementId,
			context,
			fetcher.fetch,
		],
	);
};

/**
 * Validates error is a standard Error instance. If not, wraps in new Error instance.
 */
const validateErrorFromEventType = (err: unknown, eventType: string): Error => {
	if (err instanceof Error) {
		return err;
	}
	if (typeof err === 'string') {
		return new Error(err);
	}
	return new Error(`Unknown error during ${eventType}`);
};

/**
 * Reports an error to UFO v2 for a given placement, on the current UFO v2 active interaction.
 * @param ufoV2Interaction Context of the current active UFO v2 interaction
 * @param placementId Post Office placement identifier
 * @param error
 * @param errorEventType Event type that triggered the error
 * @returns void
 */
const handleUfoV2Error = ({
	ufoV2Interaction,
	placementId,
	error,
	errorEventType,
}: {
	ufoV2Interaction: PlacementUfoV2Interaction;
	placementId: string;
	error: unknown;
	errorEventType: string;
}) => {
	const validatedError = validateErrorFromEventType(error, errorEventType);
	const { interactionId, labelStack } = ufoV2Interaction;

	if (interactionId) {
		addError(
			interactionId,
			`${errorEventType}:${placementId}`,
			labelStack || null,
			validatedError.name,
			validatedError.message,
			validatedError.stack,
		);
	} else {
		addErrorToAll(
			`${errorEventType}:${placementId}`,
			labelStack || null,
			validatedError.name,
			validatedError.message,
			validatedError.stack,
		);
	}
};

/**
 * Callback function to listen to the different stages of the placement
 */
export type PlacementOnEvent = (event: {
	type:
		| 'fetch-complete'
		| 'fetch-error'
		| 'render-complete'
		| 'render-error'
		| 'initial-data-complete'
		| 'initial-data-error'
		| 'component-ready'
		| 'component-error'
		| 'unmount';
	error?: unknown;
}) => void;

/**
 * Minimal UFO v2 interaction context used for error reporting.
 */
type PlacementUfoV2Interaction = {
	interactionId: string | null;
	labelStack?: LabelStack | null;
};

type RenderChildrenProps = {
	initialData: Promise<Response | undefined> | undefined;
	context: Context;
	onEvent: PlacementOnEvent;
};
type PropsWithRenderChildren<P = unknown> = P & {
	children: (props: RenderChildrenProps) => ReactNode;
};

type Props = PropsWithRenderChildren<BaseProps>;

const Placement: FunctionComponent<Props> = ({
	placementId,
	consumptionType,
	onEvent: placementOnEvent,
	disableInitialData = false,
	children,
}) => {
	const isUFOEnabled = true;

	const postOfficeRoute = usePostOfficeRoute();

	const context = usePostOfficeContext();

	const { baseUrl, path } = useBuildBasePath();

	const stableContextWithRoute = useStableObject({
		...context,
		postOfficeRoute,
	});

	const ufoV1PlacementTracker = useMemo(
		() =>
			isUFOEnabled
				? new UFOv1.PlacementPerformanceTracker(placementId, consumptionType)
				: undefined,
		[isUFOEnabled, placementId, consumptionType],
	);

	const ufoV2InteractionId = useContext(UFOInteractionIDContext);
	const ufoV2InteractionContext = useContext(UFOInteractionContext);
	const stableUfoV2Interaction = useStableObject({
		interactionId: ufoV2InteractionId.current,
		labelStack: ufoV2InteractionContext?.labelStack,
	});

	// Callback function to listen to the different stages of the component and track its performance
	const componentOnEvent = useCallback<PlacementOnEvent>(
		({ type, error }) => {
			// Used for local placement components, UFO v2 events
			if (consumptionType === 'local') {
				switch (type) {
					case 'fetch-error':
					case 'component-error':
					case 'render-error':
						if (fg('post-office_enable_ufo-v2-add-error')) {
							handleUfoV2Error({
								ufoV2Interaction: stableUfoV2Interaction,
								placementId,
								error,
								errorEventType: type,
							});
						}
				}
				return;
			}

			// Used for remote placement components, UFO v1 events
			if (consumptionType === 'remote') {
				placementOnEvent?.({ type, error });
				switch (type) {
					case 'fetch-complete':
						void ufoV1PlacementTracker?.markFetchComplete();
						break;
					case 'fetch-error':
						void ufoV1PlacementTracker?.markFetchFailure(validateErrorFromEventType(error, type));
						break;
					case 'render-complete':
						void ufoV1PlacementTracker?.markRenderComplete();
						break;
					case 'render-error':
						void ufoV1PlacementTracker?.markRenderFailure(validateErrorFromEventType(error, type));
						break;
					case 'initial-data-complete':
						void ufoV1PlacementTracker?.markInitialDataComplete();
						break;
					case 'initial-data-error':
						void ufoV1PlacementTracker?.markInitialDataFailure(
							validateErrorFromEventType(error, type),
						);
						break;
					case 'component-error':
						void ufoV1PlacementTracker?.markComponentFailure(
							validateErrorFromEventType(error, type),
						);
						break;
					case 'component-ready':
						void ufoV1PlacementTracker?.markComponentReady();
						break;
					case 'unmount':
						void ufoV1PlacementTracker?.markComponentAborted();
						break;
				}
			}
		},
		[placementOnEvent, ufoV1PlacementTracker, consumptionType, placementId, stableUfoV2Interaction],
	);

	const initialData = useInitialDataIfEnabled(
		placementId,
		context,
		baseUrl,
		path,
		disableInitialData,
	);

	const placementAnalyticsContext = useMemo(
		() => ({
			source: ANALYTICS_SOURCE,
			componentName: `Placement`,
			attributes: {
				placementId,
				channel: 'in-app',
			},
		}),
		[placementId],
	);

	return (
		<PostOfficeAnalyticsContext data={placementAnalyticsContext}>
			{children({
				initialData,
				context: stableContextWithRoute,
				onEvent: componentOnEvent,
			})}
		</PostOfficeAnalyticsContext>
	);
};

export default Placement;
